import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useLang } from "@/i18n/LangContext";
import { ImageOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const GallerySection = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const sectionRef = useRef(null);
  const itemRefs = useRef([]);
  const rafId = useRef(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then((res) => setItems(res.data)).catch(() => {});
  }, []);

  const placeholders = [
    { title: "Geometric", category: "Blackwork" },
    { title: "Floral", category: "Neo-traditional" },
    { title: "Script", category: "Lettering" },
    { title: "Portrait", category: "Realism" },
    { title: "Mandala", category: "Dotwork" },
    { title: "Dragon", category: "Japanese" },
  ];

  const displayItems = items.length > 0 ? items : placeholders;

  const animate = useCallback(() => {
    const el = sectionRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const sectionCenter = rect.top + rect.height / 2;
    const viewportCenter = vh / 2;
    const raw = (viewportCenter - sectionCenter) / (vh * 0.8);
    const progress = Math.max(-1, Math.min(1, raw));
    const absP = Math.abs(progress);
    const eased = absP * absP;

    itemRefs.current.forEach((node, idx) => {
      if (!node) return;
      const fromLeft = idx % 2 === 0;
      const offset = eased * 60 * (fromLeft ? -1 : 1);
      const opacity = Math.max(0, 1 - eased * 0.9);
      const scale = 1 - eased * 0.08;
      node.style.transform = `translateX(${offset}vw) scale(${scale})`;
      node.style.opacity = opacity;
    });

    rafId.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    rafId.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId.current);
  }, [animate]);

  return (
    <section
      id="gallery"
      ref={sectionRef}
      className="relative py-24 overflow-hidden"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <p
          className="text-xs tracking-[0.3em] uppercase font-bold text-purple-400 mb-3"
          style={{ fontFamily: "'Cinzel Decorative', serif" }}
        >
          {t("gallery_subtitle")}
        </p>
        <h2
          className="text-3xl md:text-5xl font-bold tracking-tight mb-16"
          data-testid="gallery-title"
        >
          {t("gallery_title")}
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {displayItems.map((item, idx) => (
            <div
              key={item.id || idx}
              ref={(el) => (itemRefs.current[idx] = el)}
              className={`relative group overflow-hidden border border-white/5 hover:border-purple-500/30 transition-colors duration-500 ${
                idx === 0 ? "row-span-2" : ""
              } ${idx === 3 ? "col-span-2 md:col-span-1" : ""}`}
              data-testid={`gallery-item-${idx}`}
              style={{ willChange: "transform, opacity" }}
            >
              {item.image_url ? (
                <img
                  src={`${BACKEND_URL}${item.image_url}`}
                  alt={item.title}
                  className="w-full h-full object-cover aspect-square group-hover:scale-105 transition-transform duration-700"
                />
              ) : (
                <div
                  className={`aspect-square flex flex-col items-center justify-center gap-3 bg-neutral-950 ${
                    idx === 0 ? "min-h-[400px] md:min-h-full" : ""
                  }`}
                >
                  <ImageOff className="w-8 h-8 text-neutral-700 group-hover:text-purple-500/50 transition-colors duration-500" />
                  <span className="text-xs tracking-[0.2em] uppercase text-neutral-600 group-hover:text-neutral-400 transition-colors duration-500">
                    {item.title}
                  </span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-4">
                <div>
                  <p className="text-white text-sm font-bold">{item.title}</p>
                  <p className="text-purple-300 text-xs tracking-wider uppercase">{item.category}</p>
                </div>
              </div>
              <div className="absolute inset-0 shadow-[inset_0_0_30px_rgba(217,70,239,0)] group-hover:shadow-[inset_0_0_30px_rgba(217,70,239,0.1)] transition-all duration-500 pointer-events-none" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
